import { Box, Divider, Typography, makeStyles } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import OutlinedFlagIcon from "@material-ui/icons/OutlinedFlag";
import clsx from "clsx";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import { useRecoilState } from "recoil";

import { User, UserUpdate } from "@/api/generated";
import { AnimationVideo } from "@/components/AnimationVideo";
import { CustomButton } from "@/components/Button";
import { DIAGNOSIS_STEP_CHIP_LABELS } from "@/components/diagnosis/diagnosisFlowStepDefinitions";
import { DiagnosisStepChip, DiagnosisStepChipsRow } from "@/components/diagnosis/DiagnosisStepChip";
import { Loading } from "@/components/Loading";
import { fontFamilies } from "@/designTokens";
import { DiagnosisLayout, diagnosisActionStyles } from "@/layouts/DiagnosisLayout";
import { RegistrationTemplate } from "@/layouts/RegistrationTemplate";
import { getInvestmentDiagnosisResult } from "@/logics/investmentDiagnosis";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { isExpoRegistration } from "@/utilities/expoRegistration";
import { UserState } from "@/view_models/atoms";

import SkylineImage from "@assets/image/diagnosis_skyline.png";
import IntroImage from "@assets/image/investment_diagnosis_step_01.png";
import PropertyImage from "@assets/image/investment_diagnosis_step_02_property.png";
import LoadingVideo from "@assets/image/investment_diagnosis_step_loading.mp4";
import LoadingPoster from "@assets/image/investment_diagnosis_step_loading_poster.jpg";

import { AnimatedResultAmount } from "./components/AnimatedResultAmount";
import { WealthCalculationDetails } from "./components/WealthCalculationDetails";
import { buildInvestmentDiagnosisNextStep } from "./utils/nextStep";

type DiagnosisStep = "intro" | "loading" | "resultHigh" | "resultLow";

const STEP_EVENT_MAP: Record<DiagnosisStep, AppEventTokenEnum> = {
  intro: AppEventTokenEnum.InvestmentDiagnosisStep1,
  loading: AppEventTokenEnum.InvestmentDiagnosisStep2Loading,
  resultHigh: AppEventTokenEnum.InvestmentDiagnosisStep2OK,
  resultLow: AppEventTokenEnum.InvestmentDiagnosisStep2NG,
};

const useStyles = makeStyles((theme) => ({
  "@keyframes resultEnter": {
    from: { opacity: 0, transform: "translateY(12px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes resultCardEnter": {
    from: { opacity: 0, transform: "translateY(20px) scale(0.985)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  resultReveal: {
    animation: "$resultEnter 480ms cubic-bezier(0.22, 1, 0.36, 1) both",
    "@media (prefers-reduced-motion: reduce)": { "&&": { animation: "none" } },
  },
  cardReveal: {
    animationName: "$resultCardEnter",
    animationDuration: "620ms",
    animationDelay: "100ms",
  },
  attributesReveal: { animationDelay: "260ms" },
  propertyReveal: { animationDelay: "440ms" },
  conclusionReveal: { animationDelay: "560ms" },
  actionsReveal: {
    animationDelay: "640ms",
    "&:focus-within": { animation: "none" },
  },
  root: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(5, 2.5),
    overflowX: "hidden",
    backgroundColor: theme.palette.background.default,
  },
  rootLoading: {
    backgroundColor: theme.palette.background.paper,
    paddingTop: theme.spacing(20),
    paddingLeft: 0,
    paddingRight: 0,
  },
  resultBackdrop: {
    position: "relative",
    "&:before": {
      content: "\"\"",
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      height: 259,
      backgroundImage: `linear-gradient(20.68deg, ${theme.palette.semantic["surface/hero-start"]} 18%, ${theme.palette.semantic["surface/trust"]} 82%)`,
      backgroundSize: "cover",
      backgroundPosition: "center bottom",
      [theme.breakpoints.up("md")]: { height: 320 },
    },
    "&:after": {
      content: "\"\"",
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      height: 259,
      backgroundImage: `url(${SkylineImage})`,
      backgroundPosition: "center bottom",
      backgroundSize: "cover",
      opacity: 0.29,
      mixBlendMode: "soft-light",
      pointerEvents: "none",
      [theme.breakpoints.up("md")]: { height: 320 },
    },
  },
  card: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: 20,
    textAlign: "left",
    boxShadow: "0 4px 12px rgba(0,16,155,.1)",
    marginTop: 16,
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    lineHeight: 1.4,
    textAlign: "center",
  },
  description: {
    marginTop: 8,
    textAlign: "center",
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.palette.common.white,
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  conclusion: {
    fontSize: 12,
    lineHeight: 1.5,
    marginTop: 16,
    color: theme.palette.text.secondary,
    textAlign: "center",
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
  buttonWrapper: { marginBottom: theme.spacing(2) },
  resultButtonWrapper: diagnosisActionStyles,
  buttonText: {
    flexGrow: 1,
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
  },
  resultWrapper: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 608,
    margin: "0 auto",
    padding: "32px 24px 0",
    textAlign: "left",
    [theme.breakpoints.up("md")]: { paddingTop: 48 },
  },
  resultHeading: {
    marginTop: 24,
    fontWeight: 700,
    fontSize: 20,
    lineHeight: 1.3,
    color: theme.palette.common.white,
    textAlign: "center",
    [theme.breakpoints.up("md")]: { fontSize: 24 },
  },
  potentialCard: {
    borderRadius: 8,
    backgroundColor: theme.palette.semantic["surface/base"],
    padding: 16,
    marginBottom: 8,
  },
  price: { padding: 0, backgroundColor: "transparent", marginBottom: 16 },
  potentialValueRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  potentialLabel: {
    fontWeight: 500,
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  potentialValueNumber: {
    fontFamily: fontFamilies.number,
    fontWeight: 600,
    fontSize: 32,
    lineHeight: 1.2,
    letterSpacing: "-.03em",
    color: theme.palette.primary.main,
  },
  potentialValueUnit: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  },
  potentialValueSuffix: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.5,
    letterSpacing: 0.42,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },
  attributeChipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: 16,
  },
  attributeChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "0 12px",
    height: 32,
    borderRadius: 9999,
    border: `1px solid ${theme.palette.grey[300]}`,
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.common.white,
  },
  cardDivider: {
    margin: "16px 0",
  },
  sectionLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.semantic["text/heading"],
    marginBottom: 12,
  },
  resultLowCommentText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  resultLowFutureTitle: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.5,
    letterSpacing: 0.42,
    marginBottom: theme.spacing(1),
  },
  resultLowFootnote: {
    fontSize: 12,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  exampleLabel: {
    color: theme.palette.semantic["text/heading"],
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: theme.spacing(1.5),
  },
  propertyCard: {
    backgroundColor: theme.palette.common.white,
    display: "flex",
  },
  propertyImage: {
    width: 88,
    height: 131,
    borderRadius: 8,
    objectFit: "cover",
  },
  propertyTextBox: {
    flex: 1,
    padding: "0 0 0 12px",
    minWidth: 0,
    gap: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  propertyTitle: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.5,
  },
  propertyText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  propertySpeedLabelWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  propertySpeedLabel: {
    fontWeight: 500,
    fontSize: 12,
    lineHeight: 1.5,
  },
  propertySpeedValue: {
    display: "flex",
    fontWeight: 700,
    fontSize: 20,
    fontFamily: fontFamilies.number,
    flexWrap: "wrap",
    alignItems: "baseline",
    lineHeight: 1.2,
    letterSpacing: 0.54,
    color: theme.palette.primary.main,
  },
  propertySpeedValueMeta: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.5,
    letterSpacing: 0.42,
    color: theme.palette.text.primary,
    gap: theme.spacing(0.5),
  },
  wealthNote: {
    fontSize: 12,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  adviceCard: {
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
    boxShadow: "0px 0px 16px 0px rgba(0, 0, 0, 0.15)",
    marginBottom: theme.spacing(5),
    textAlign: "center",
  },
  adviceImage: {
    width: "100%",
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
  adviceText: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.8,
    letterSpacing: 0.28,
    textAlign: "center",
  },
  speedRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  introContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    textAlign: "center",
  },
  insightBody: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.5,
    letterSpacing: 0.42,
  },
  introTitle: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 24,
    lineHeight: 1.5,
    letterSpacing: 0.72,
    marginBottom: theme.spacing(2),
  },
  introDescription: {
    whiteSpace: "pre-line",
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.5,
    letterSpacing: 0.48,
    marginBottom: theme.spacing(5),
  },
  introImage: {
    height: 291,
    // テンプレートの左右padding(24px=spacing(3))に合わせて画面端までブリードさせる
    margin: theme.spacing(0, -3, 5, -3),
    display: "block",
  },
  loadingWrapper: {
    width: "100%",
    maxWidth: 600,
    textAlign: "center",
    maskImage: "linear-gradient(to bottom, transparent 0%, black 5%, black 90%, transparent 100%)",
    WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 5%, black 90%, transparent 100%)",
  },
  loadingVideo: {
    width: "100%",
  },
  illustration: {
    height: 160,
    borderRadius: 12,
    marginBottom: theme.spacing(3),
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%), ${theme.palette.semantic["surface/base"]}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  resultCard: {
    borderRadius: 16,
    backgroundColor: theme.palette.semantic["status/info-bg"],
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    textAlign: "left",
  },
  resultLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
}));

export const DiagnosisPage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useRecoilState(UserState);
  const [step, setStep] = useState<DiagnosisStep>("intro");
  const initialStepResolvedRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const skipAutoRedirectRef = useRef(false);
  const api = useMemo(() => PropallyApiFactory(), []);
  // 入口の種別（通常 / エキスポ導線）は表示中に変わらないため、初回描画時に1回だけ読む。
  // 次の遷移先・ステップチップ・案内文の分岐はすべてこの値から決め、画面内で判定がずれないようにする
  const isExpo = useMemo(() => isExpoRegistration(), []);
  const nextStep = useMemo(() => buildInvestmentDiagnosisNextStep(isExpo), [isExpo]);

  const { priceEstimate, resultLowComment } = getInvestmentDiagnosisResult(user);
  const shouldShowResultLow = resultLowComment !== null;
  const RENT_FACTOR = 0.0038; // 家賃係数（Pの0.38%が家賃）
  const LOAN_RATE = 0.023 / 12; // 年利
  const LOAN_MONTHS = 420; // 返済月（35年）
  const MANAGEMENT_FEE = 1.5; // 管理費
  const GAIN_RATE_BASE = 0.0652; // 売却益率の基準値
  const GAIN_RATE_SLOPE = 0.00000567; // 売却益率の減少ペース
  const GAIN_RATE_MIN = 0.01; // 売却益率の最小値
  const SIMULATION_MONTHS = 72; // シミュレーションタイミング
  const wealthSpeedDetails = useMemo(() => {
    if (!priceEstimate) return null;
    const principal = priceEstimate;
    const rate = LOAN_RATE;
    const months = LOAN_MONTHS;
    const loanPayment = rate === 0 ? principal / months : (principal * rate) / (1 - Math.pow(1 + rate, -months));
    const rent = principal * RENT_FACTOR;
    const monthlyCF = rent - (loanPayment + MANAGEMENT_FEE);
    const gainRate = Math.max(GAIN_RATE_MIN, GAIN_RATE_BASE - GAIN_RATE_SLOPE * principal);
    const capitalGain = principal * gainRate;
    const income6yr = monthlyCF * SIMULATION_MONTHS;
    const speed = (income6yr + capitalGain) / SIMULATION_MONTHS;
    return {
      speed: Math.round(speed * 100) / 100,
      principal,
      rent,
      loanPayment,
      monthlyCF,
      gainRate,
      capitalGain,
      income6yr,
    };
  }, [priceEstimate]);
  const monthlyWealthSpeed = wealthSpeedDetails?.speed ?? null;
  const formattedMonthlyWealth = useMemo(() => {
    if (monthlyWealthSpeed === null) return null;
    const yenValue = monthlyWealthSpeed * 10000;
    const rounded = Math.round(yenValue);
    const sign = rounded >= 0 ? "+" : "-";
    return `${sign}${Intl.NumberFormat("ja-JP").format(Math.abs(rounded))}`;
  }, [monthlyWealthSpeed]);
  const attributeChips = useMemo(() => {
    const chips: string[] = [];
    const rawIncome = user?.annual_income;
    if (rawIncome !== null && rawIncome !== undefined && Number(rawIncome) > 0) {
      chips.push(`${Math.floor(Number(rawIncome) / 100) * 100}万円台`);
    }
    if (user?.birthday) {
      const birthDate = dayjs(user.birthday);
      if (birthDate.isValid()) {
        const age = dayjs().diff(birthDate, "year");
        if (age >= 0) {
          const decade = Math.floor(age / 10) * 10;
          const half = age % 10 < 5 ? "前半" : "後半";
          chips.push(`${decade}代${half}`);
        }
      }
    }
    if (user?.job_category) {
      chips.push(user.job_category);
    }
    if (user?.has_investment_experience === false) {
      chips.push("投資未経験");
    } else if (user?.has_investment_experience === true) {
      chips.push("投資経験あり");
    }
    return chips;
  }, [user]);

  useEffect(() => {
    if (!user || initialStepResolvedRef.current) return;
    setStep(shouldShowResultLow ? "resultLow" : "resultHigh");
    initialStepResolvedRef.current = true;
  }, [shouldShowResultLow, user]);

  useEffect(() => {
    if (!user) return;
    if (user.is_diagnosis_complete === false) return;
    if (skipAutoRedirectRef.current) return;
    history.replace("/mypage");
  }, [user, history]);

  useEffect(() => {
    if (!initialStepResolvedRef.current) return;
    const eventToken = STEP_EVENT_MAP[step];
    if (!eventToken) return;
    sendEventToAppMeasurementTool(eventToken);
  }, [step]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  const handleLoadingFinish = useCallback(() => {
    setStep(shouldShowResultLow ? "resultLow" : "resultHigh");
  }, [shouldShowResultLow]);

  useEffect(() => {
    if (step !== "loading") return;
    const timeoutId = window.setTimeout(handleLoadingFinish, 8000);
    return () => window.clearTimeout(timeoutId);
  }, [step, handleLoadingFinish]);

  if (!user) {
    return <Loading />;
  }

  if (!initialStepResolvedRef.current) {
    return <Loading />;
  }

  if (user.is_diagnosis_complete !== false) {
    return null;
  }

  const startDiagnosis = () => {
    setStep("loading");
  };

  const completeDiagnosis = async (nextPath: string) => {
    if (submitting) return;
    const completionEvent =
      step === "resultLow"
        ? AppEventTokenEnum.InvestmentDiagnosisCompleteNG
        : AppEventTokenEnum.InvestmentDiagnosisCompleteOK;
    sendEventToAppMeasurementTool(completionEvent);
    skipAutoRedirectRef.current = true;
    if (user.is_diagnosis_complete === true) {
      history.push(nextPath);
      return;
    }
    setSubmitting(true);
    try {
      const payload: UserUpdate = { is_diagnosis_complete: true };
      const response = await api.v1UserPatch(payload, { withCredentials: true });
      setUser(response.data as User);
      history.push(nextPath);
    } catch (error) {
      skipAutoRedirectRef.current = false;
      enqueueSnackbar("診断結果の保存に失敗しました。時間を置いて再度お試しください。", { variant: "error" });
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    switch (step) {
      case "intro":
        return (
          <Box className={classes.introContainer}>
            <Typography className={classes.introTitle}>プロパリーへようこそ！</Typography>
            <Typography className={classes.introDescription}>
              {"プロパリーは、不動産投資をはじめる人の\n最初の一歩を失敗させないサービスです。"}
            </Typography>
            <img src={IntroImage} alt="診断ステップ1" className={classes.introImage} />
            <div className={classes.buttonWrapper}>
              <CustomButton customVariant="filled" onClick={startDiagnosis} fullWidth>
                <Box className={classes.buttonText}>あなたの投資力は？</Box>
                <ArrowForwardIosIcon fontSize="small" />
              </CustomButton>
            </div>
          </Box>
        );
      case "loading":
        return (
          <Box className={classes.loadingWrapper}>
            <AnimationVideo
              className={classes.loadingVideo}
              src={LoadingVideo}
              poster={LoadingPoster}
              onEnded={handleLoadingFinish}
              onError={handleLoadingFinish}
            />
          </Box>
        );
      case "resultHigh":
        return (
          <Box className={classes.resultWrapper}>
            <Box className={classes.resultReveal}>
              <DiagnosisStepChipsRow>
                <DiagnosisStepChip v2 label={DIAGNOSIS_STEP_CHIP_LABELS.investment} state="completed" withCheck />
                {/* エキスポ導線では投資スタイル診断を飛ばすため、受けないステップのチップは出さない */}
                {!isExpo && (
                  <DiagnosisStepChip v2 label={DIAGNOSIS_STEP_CHIP_LABELS.style} state="upcoming" withCheck={false} />
                )}
                <DiagnosisStepChip v2 label={DIAGNOSIS_STEP_CHIP_LABELS.planner} state="upcoming" withCheck={false} />
              </DiagnosisStepChipsRow>
              <Typography className={classes.resultHeading}>あなたの投資力診断結果</Typography>
              <Typography className={classes.description}>マイページからいつでも確認できます</Typography>
            </Box>
            <Box className={clsx(classes.card, classes.resultReveal, classes.cardReveal)}>
              <Box className={`${classes.potentialCard} ${classes.price}`}>
                <Typography className={classes.potentialLabel}>検討できる物件価格の目安</Typography>
                <Box className={classes.potentialValueRow}>
                  <Typography className={classes.potentialValueNumber}>
                    <AnimatedResultAmount value={priceEstimate} />
                  </Typography>
                  <Typography className={classes.potentialValueUnit}>万円</Typography>
                  <Typography className={classes.potentialValueSuffix}>前後</Typography>
                </Box>
              </Box>

              {attributeChips.length > 0 && (
                <Box className={clsx(classes.attributeChipsRow, classes.resultReveal, classes.attributesReveal)}>
                  {attributeChips.map((chip) => (
                    <span key={chip} className={classes.attributeChip}>
                      {chip}
                    </span>
                  ))}
                </Box>
              )}

              <Divider className={classes.cardDivider} />

              <Box className={clsx(classes.resultReveal, classes.propertyReveal)}>
                <Typography className={classes.exampleLabel}>この価格帯で検討できる物件イメージ</Typography>
                <Box className={classes.propertyCard}>
                  <img src={PropertyImage} alt="物件例" className={classes.propertyImage} />
                  <Box className={classes.propertyTextBox}>
                    <Box>
                      <Typography className={classes.propertyTitle}>東京23区 人気エリア</Typography>
                      <Typography className={classes.propertyText}>駅徒歩10分以内</Typography>
                    </Box>
                    <Box className={classes.propertySpeedLabelWrapper}>
                      <Typography className={classes.propertySpeedLabel}>月あたりの資産形成効果</Typography>
                      <Typography className={classes.propertySpeedValue}>
                        {formattedMonthlyWealth ?? "計算中"}
                        <span className={classes.propertySpeedValueMeta}> 円 / 月</span>
                      </Typography>
                      <Typography className={classes.wealthNote}>※毎月の収支＋想定売却益の月割りによる概算</Typography>
                    </Box>
                  </Box>
                </Box>
                {wealthSpeedDetails && (
                  <WealthCalculationDetails
                    details={wealthSpeedDetails}
                    annualLoanRate={LOAN_RATE * 12}
                    repaymentMonths={LOAN_MONTHS}
                    managementFee={MANAGEMENT_FEE}
                    simulationMonths={SIMULATION_MONTHS}
                  />
                )}
              </Box>
            </Box>

            <Typography className={clsx(classes.conclusion, classes.resultReveal, classes.conclusionReveal)}>
              現在の入力内容では、上記の価格帯が検討の目安となります。
            </Typography>

            <div className={clsx(classes.resultButtonWrapper, classes.resultReveal, classes.actionsReveal)}>
              <CustomButton
                tone="trust"
                buttonSize="L"
                onClick={() => completeDiagnosis(nextStep.path)}
                fullWidth
              >
                <Box className={classes.buttonText}>{nextStep.label}</Box>
              </CustomButton>
            </div>
          </Box>
        );
      case "resultLow":
        return (
          <Box className={classes.resultWrapper}>
            <DiagnosisStepChipsRow>
              <DiagnosisStepChip v2 label={DIAGNOSIS_STEP_CHIP_LABELS.investment} state="completed" withCheck />
              {/* エキスポ導線では投資スタイル診断を飛ばすため、受けないステップのチップは出さない */}
              {!isExpo && (
                <DiagnosisStepChip v2 label={DIAGNOSIS_STEP_CHIP_LABELS.style} state="upcoming" withCheck={false} />
              )}
              <DiagnosisStepChip v2 label={DIAGNOSIS_STEP_CHIP_LABELS.planner} state="upcoming" withCheck={false} />
            </DiagnosisStepChipsRow>
            <Typography className={classes.resultHeading}>投資力診断結果</Typography>
            <Typography className={classes.description}>
              まずは「どのくらいの規模で始められるか」を診断しました
            </Typography>
            <Box className={classes.card}>
              <Typography className={classes.sectionLabel}>
                <ChatBubbleOutlineIcon fontSize="small" />
                診断コメント
              </Typography>
              <Box className={classes.potentialCard}>
                <Typography className={classes.resultLowCommentText}>{resultLowComment}</Typography>
              </Box>
              <Typography className={classes.resultLowFootnote}>
                ※あくまで診断上の目安です。ご状況によっては、融資枠をご用意できる場合があります。
              </Typography>

              <Divider className={classes.cardDivider} />

              {isExpo ? (
                // エキスポ導線では次にスタイル診断を受けないため「診断しておきましょう」の案内は出さず、
                // あとからホームで受けられることだけを伝える（CTA はプランナー紹介へ進む）
                <Typography className={classes.insightBody}>
                  プランナーとマッチしたあとでも、ホームから投資スタイル診断を受けられます。
                </Typography>
              ) : (
                <>
                  <Typography className={classes.sectionLabel}>
                    <OutlinedFlagIcon fontSize="small" />
                    今後、投資を始めるときのために
                  </Typography>
                  <Typography className={classes.resultLowFutureTitle}>
                    あなたに合う「投資スタイル」を診断しておきましょう
                  </Typography>
                  <Typography className={classes.insightBody}>
                    条件が整ったときにスムーズに動けるよう、どんな投資と相性がいいかを今のうちに把握できます。
                  </Typography>
                </>
              )}
            </Box>

            <div className={classes.resultButtonWrapper}>
              <CustomButton
                tone="trust"
                buttonSize="L"
                onClick={() => completeDiagnosis(nextStep.path)}
                fullWidth
              >
                <Box className={classes.buttonText}>{nextStep.label}</Box>
              </CustomButton>
            </div>
          </Box>
        );
      default:
        return null;
    }
  };

  // ローディングは背景paper・上160px・動画フル幅マスクの特殊レイアウトのためテンプレート外で描画する
  if (step === "loading") {
    return <Box className={clsx(classes.root, classes.rootLoading)}>{renderContent()}</Box>;
  }

  if (step === "resultHigh" || step === "resultLow") {
    return (
      <DiagnosisLayout mode="full" publicHeader>
        <Box className={classes.resultBackdrop}>{renderContent()}</Box>
      </DiagnosisLayout>
    );
  }
  return <RegistrationTemplate>{renderContent()}</RegistrationTemplate>;
};
